import dayjs from "dayjs";

import { toLayoutRequest } from "./report-layout";

export const DEFAULT_REPORT_FILTERS = {
  dateRange: [null, null],
  // null means every warehouse currently mapped to the outlet.
  warehouseIds: null,
  search: "",
  product: null,
  category: null,
  hideZeroRows: true,
  sortField: "sku",
  sortDirection: "asc",
};

export const MOVEMENT_BLOCK_OPTIONS = [
  { value: "opening", label: "Saldo Awal" },
  { value: "in", label: "Masuk" },
  { value: "out", label: "Keluar" },
  { value: "closing", label: "Saldo Akhir" },
];

const isValidDay = (value) => dayjs.isDayjs(value) && value.isValid();

/** Quick periods offered next to the calendar; none of them ends in the future. */
export const getReportDatePresets = (today = dayjs()) => {
  const lastMonth = today.subtract(1, "month");
  return [
    { label: "Bulan ini", getValue: () => [today.startOf("month"), today] },
    {
      label: "Bulan lalu",
      getValue: () => [lastMonth.startOf("month"), lastMonth.endOf("month")],
    },
    {
      label: "3 bulan terakhir",
      getValue: () => [today.subtract(2, "month").startOf("month"), today],
    },
    { label: "Tahun ini", getValue: () => [today.startOf("year"), today] },
  ];
};

/** Filters a fresh page starts with, so the report can load without any input. */
export const getInitialReportFilters = (today = dayjs()) => ({
  ...DEFAULT_REPORT_FILTERS,
  dateRange: [today.startOf("month"), today],
});

export const validateReportFilters = ({
  outlet,
  filters,
  options,
  today = dayjs(),
}) => {
  const errors = {};
  if (!outlet?.id) errors.outlet = "Pilih outlet.";

  const [start, end] = filters.dateRange;
  if (!isValidDay(start) || !isValidDay(end)) {
    errors.dateRange = "Pilih tanggal mulai dan tanggal akhir.";
  } else if (start.isAfter(end, "day")) {
    errors.dateRange = "Tanggal mulai tidak boleh setelah tanggal akhir.";
  } else if (end.isAfter(today, "day")) {
    errors.dateRange = "Tanggal akhir tidak boleh melewati hari ini.";
  } else if (
    options &&
    end.startOf("day").diff(start.startOf("day"), "day") + 1 >
      options.limits.maxPeriodDays
  ) {
    errors.dateRange = `Periode maksimal ${options.limits.maxPeriodDays} hari.`;
  }

  if (filters.warehouseIds !== null) {
    if (filters.warehouseIds.length === 0) {
      errors.warehouses = "Pilih gudang atau kosongkan untuk semua gudang.";
    } else if (
      options &&
      filters.warehouseIds.length > options.limits.maxWarehouseIds
    ) {
      errors.warehouses = `Maksimal ${options.limits.maxWarehouseIds} gudang.`;
    }
  }

  if (options && filters.search.length > options.limits.maxSearchLength) {
    errors.search = `Maksimal ${options.limits.maxSearchLength} karakter.`;
  }

  return errors;
};

export const buildPreviewBody = ({ filters, layout, options }) => {
  const [start, end] = filters.dateRange;
  return {
    schemaVersion: options.schemaVersion,
    startDate: start.format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
    ...(filters.warehouseIds ? { warehouseIds: [...filters.warehouseIds] } : {}),
    filters: {
      search: filters.search.trim(),
      productId: filters.product?.id ?? null,
      categoryId: filters.category?.id ?? null,
      hideZeroRows: filters.hideZeroRows,
    },
    sort: { field: filters.sortField, direction: filters.sortDirection },
    layout: toLayoutRequest(layout),
  };
};

/** Detail uses exactly the applied summary dates and number display. */
export const buildMovementBody = ({
  appliedBody,
  warehouseId,
  sku,
  block,
  page,
  pageSize,
}) => ({
  schemaVersion: appliedBody.schemaVersion,
  startDate: appliedBody.startDate,
  endDate: appliedBody.endDate,
  warehouseId,
  sku,
  block,
  page,
  pageSize,
  moneyDecimals: appliedBody.layout.moneyDecimals,
  negativeFormat: appliedBody.layout.negativeFormat,
});

/**
 * Stable comparison key used to mark the applied report stale after filter edits.
 * Sort is a display choice applied from the layout dialog, so it is left out.
 */
// eslint-disable-next-line no-unused-vars
export const getFiltersSignature = ({ sortField, sortDirection, ...filters }) =>
  JSON.stringify({
    ...filters,
    dateRange: filters.dateRange.map((value) =>
      isValidDay(value) ? value.format("YYYY-MM-DD") : null
    ),
    search: filters.search.trim(),
    product: filters.product?.id ?? null,
    category: filters.category?.id ?? null,
  });
